import { useCallback } from 'react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useLocation, useNavigate } from 'react-router-dom';
import { flowUrlParams } from '../_shared/url-params';

// `?tab=` — same nuqs convention as core's web/app-home/src/lib/urlParams.ts
// (parseAsStringLiteral(...).withDefault(...)). Keeps the subtab selection
// alive across a full CommandCenterApp remount (nav away + back, page
// refresh) and makes it shareable — a plain useState resets to 'account'
// on every remount.
export const subtabValues = ['account', 'contact', 'plays', 'signals'] as const;
export type Subtab = (typeof subtabValues)[number];
export const DEFAULT_SUBTAB: Subtab = 'account';

export const SUBTABS: Array<{ key: Subtab; label: string; hidden?: boolean }> = [
  { key: 'account', label: 'Accounts' },
  { key: 'contact', label: 'Contacts' },
  // Unhidden 2026-09-22 — the terra_signals performance work (2026-09-13)
  // and the "Actioned By" gap this replaces both landed. `subtabValues`/
  // `CommandCenterHome`'s switch are unchanged, so `?tab=plays` keeps
  // working exactly as it did while this was hidden.
  { key: 'plays', label: 'Plays' },
  // Unhidden 2026-09-28 — Spencer signed off. Like Plays above, the
  // `?tab=signals` link behaves exactly as it did while hidden.
  { key: 'signals', label: 'Signals' },
];
export const VISIBLE_SUBTABS = SUBTABS.filter((tab) => !tab.hidden);

/**
 * `?tab=`/`?q=` state + the subtab-change navigation rule, shared by both
 * shells (old `CommandCenterApp` and the new `shell-next/Header` via
 * `FlowShell`'s `CommandCenterBody`) so a tab click lands on identical URL
 * state by construction — one place owns the `?tab=` literals that
 * `mini-apps/_shared/flow-links.ts` depends on.
 */
export function useCommandCenterSubtab(appBase: string): {
  subtab: Subtab;
  onHome: boolean;
  setSubtab: (t: Subtab) => void;
  initialQuery: string | null;
} {
  const [subtab, setSubtabState] = useQueryState(
    'tab',
    parseAsStringLiteral(subtabValues).withDefault(DEFAULT_SUBTAB),
  );
  // `?q=` — short entity-entry routes' search fallback (plan D5,
  // `web/app-home/src/components/flow/entity-routes.tsx`'s `SearchRedirect`/
  // `ContactRedirect`). READ-ONLY here, same transport family as `?tab=`
  // above — this hook never writes or clears `q`. `GlobalSearch` never
  // unmounts across a shell's internal route changes, so it re-seeds its
  // local state (`initialQuery`) on mount AND again on every subsequent
  // render where THIS value changes to a new truthy string (e.g. Back
  // navigation restoring `?q=` after a result select had cleared it) — see
  // `GlobalSearch`'s `initialQuery` prop doc comment for the render-time
  // sync mechanics.
  const [initialQuery] = useQueryState('q', flowUrlParams.q);
  const navigate = useNavigate();
  const location = useLocation();

  // Subtab click from the pinned bar: on the home route it's a plain state
  // write; from the drill-down it navigates home carrying the tab explicitly
  // (the raw nuqs setter alone would write the param onto the drill-down URL,
  // then be lost by the navigation).
  const onHome = location.pathname === appBase || location.pathname === `${appBase}/`;

  const setSubtab = useCallback(
    (t: Subtab) => {
      if (onHome) {
        // nuqs's setter returns a Promise (it resolves once the URL write
        // settles) — nothing here awaits the write or needs to react to it
        // landing.
        void setSubtabState(t);
      } else {
        navigate(`${appBase}?tab=${t}`);
      }
    },
    [onHome, setSubtabState, navigate, appBase],
  );

  return { subtab, onHome, setSubtab, initialQuery };
}
